/**
 * Utility functions for time conversion and overnight shift detection
 * Based on the Flutter logic from the reference code
 */

/**
 * Convert time object to 24-hour format for comparison
 * @param {Object} time - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {number} - Total minutes from midnight (0-1439)
 */
export const timeToMinutes = (time) => {
  let hours = time.hours;
  const minutes = time.minutes;
  
  // Convert to 24-hour format
  if (time.period === 'AM' && hours === 12) {
    hours = 0; // 12 AM = 0 hours
  } else if (time.period === 'PM' && hours !== 12) {
    hours += 12; // PM hours (except 12 PM)
  }
  
  return hours * 60 + minutes;
};

/**
 * Convert minutes from midnight back to time object
 * @param {number} minutes - Total minutes from midnight (0-1439)
 * @returns {Object} - { hours: number, minutes: number, period: 'AM'|'PM' }
 */
export const minutesToTime = (minutes) => {
  let hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  
  // Convert to 12-hour format
  let period = 'AM';
  if (hours >= 12) {
    period = 'PM';
    if (hours > 12) {
      hours -= 12;
    }
  }
  if (hours === 0) {
    hours = 12; // 0 hours = 12 AM
  }
  
  return {
    hours,
    minutes: mins,
    period
  };
};

/**
 * Check if a shift is an overnight shift (crosses midnight)
 * @param {Object} startTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @param {Object} endTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {boolean} - true if the shift crosses midnight
 */
export const isOvernightShift = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return false;
  }
  
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  
  // If end time is earlier than start time, it's an overnight shift
  return endMinutes < startMinutes;
};

/**
 * Format time for display
 * @param {Object} time - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {string} - Formatted time string (e.g., "09:00 PM")
 */
export const formatTimeDisplay = (time) => {
  if (!time) return '';
  
  const hours = time.hours.toString().padStart(2, '0');
  const minutes = time.minutes.toString().padStart(2, '0');
  return `${hours}:${minutes} ${time.period}`;
};

/**
 * Parse time string to time object
 * @param {string} timeStr - Time string in format "HH:MM AM/PM" or "H:MM AM/PM"
 * @returns {Object|null} - { hours: number, minutes: number, period: 'AM'|'PM' } or null if invalid
 */
export const parseTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') {
    return null;
  }
  
  // Match patterns like "9:00 PM", "09:00 AM", "12:30 PM", etc.
  const match = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  
  if (!match) {
    return null;
  }
  
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  
  // Validate hours and minutes
  if (hours < 1 || hours > 12 || minutes < 0 || minutes > 59) {
    return null;
  }
  
  return { hours, minutes, period };
};

/**
 * Get shift duration in hours
 * @param {Object} startTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @param {Object} endTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {number} - Duration in hours (can be > 24 for overnight shifts)
 */
export const getShiftDuration = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return 0;
  }
  
  const startMinutes = timeToMinutes(startTime);
  let endMinutes = timeToMinutes(endTime);
  
  // If it's an overnight shift, add 24 hours to end time
  if (isOvernightShift(startTime, endTime)) {
    endMinutes += 24 * 60; // Add 24 hours in minutes
  }
  
  return (endMinutes - startMinutes) / 60;
};

/**
 * Get shift type based on start time
 * @param {Object} startTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {string} - 'morning', 'afternoon', 'night', or 'overnight'
 */
export const getShiftType = (startTime) => {
  if (!startTime) return 'morning';
  
  const startMinutes = timeToMinutes(startTime);
  
  // Define shift boundaries (in minutes from midnight)
  const morningStart = timeToMinutes({ hours: 6, minutes: 0, period: 'AM' });   // 6:00 AM
  const afternoonStart = timeToMinutes({ hours: 12, minutes: 0, period: 'PM' }); // 12:00 PM
  const nightStart = timeToMinutes({ hours: 6, minutes: 0, period: 'PM' });      // 6:00 PM
  const overnightStart = timeToMinutes({ hours: 10, minutes: 0, period: 'PM' }); // 10:00 PM
  
  if (startMinutes >= morningStart && startMinutes < afternoonStart) {
    return 'morning';
  } else if (startMinutes >= afternoonStart && startMinutes < nightStart) {
    return 'afternoon';
  } else if (startMinutes >= nightStart && startMinutes < overnightStart) {
    return 'night';
  } else {
    return 'overnight';
  }
};

/**
 * Generate proper timestamps for overnight shifts across date ranges
 * @param {Object} startTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @param {Object} endTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @param {Date} targetDate - The base date for the shift
 * @returns {Object} - { from_timestamp: string, to_timestamp: string, is_overnight: boolean }
 */
export const generateShiftTimestamps = (startTime, endTime, targetDate) => {
  if (!startTime || !endTime || !targetDate) {
    return {
      from_timestamp: '',
      to_timestamp: '',
      is_overnight: false
    };
  }

  // Convert time objects to 24-hour format
  let startHour = startTime.hours;
  let endHour = endTime.hours;
  
  if (startTime.period === 'AM' && startTime.hours === 12) {
    startHour = 0;
  } else if (startTime.period === 'PM' && startTime.hours !== 12) {
    startHour += 12;
  }
  
  if (endTime.period === 'AM' && endTime.hours === 12) {
    endHour = 0;
  } else if (endTime.period === 'PM' && endTime.hours !== 12) {
    endHour += 12;
  }

  // Create start datetime
  const startDateTime = new Date(
    targetDate.getFullYear(), 
    targetDate.getMonth(), 
    targetDate.getDate(), 
    startHour, 
    startTime.minutes, 
    0
  );

  // Determine if this is an overnight shift
  const isOvernight = endHour < startHour || 
                     (endHour === startHour && endTime.minutes < startTime.minutes);

  // Create end datetime (next day if overnight)
  let endDate = targetDate;
  if (isOvernight) {
    endDate = new Date(targetDate.getTime() + 24 * 60 * 60 * 1000); // Add 1 day
  }

  const endDateTime = new Date(
    endDate.getFullYear(), 
    endDate.getMonth(), 
    endDate.getDate(), 
    endHour, 
    endTime.minutes, 
    0
  );

  // Format timestamps with timezone offset like Flutter implementation
  const formatDateTimeWithTimezone = (dateTime) => {
    const timezoneOffset = dateTime.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
    const offsetMinutes = Math.abs(timezoneOffset) % 60;
    const offsetString = timezoneOffset <= 0 
      ? `+${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`
      : `-${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
    
    return `${dateTime.toISOString().split('.')[0]}.000000${offsetString}`;
  };

  return {
    from_timestamp: formatDateTimeWithTimezone(startDateTime),
    to_timestamp: formatDateTimeWithTimezone(endDateTime),
    is_overnight: isOvernight
  };
};

/**
 * Validate time range
 * @param {Object} startTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @param {Object} endTime - { hours: number, minutes: number, period: 'AM'|'PM' }
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateTimeRange = (startTime, endTime) => {
  if (!startTime || !endTime) {
    return { isValid: false, error: 'Both start and end times are required' };
  }
  
  // Check if times are the same
  if (startTime.hours === endTime.hours && 
      startTime.minutes === endTime.minutes && 
      startTime.period === endTime.period) {
    return { isValid: false, error: 'Start time and end time cannot be the same' };
  }
  
  // For non-overnight shifts, check if end time is after start time
  if (!isOvernightShift(startTime, endTime)) {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = timeToMinutes(endTime);
    
    if (endMinutes <= startMinutes) {
      return { isValid: false, error: 'End time must be after start time for same-day shifts' };
    }
  }
  
  return { isValid: true, error: null };
};
